import { useEffect, useState, useRef } from 'react';
import { AlertService } from '../../../../services/alert.service';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CampaignsService } from '../../../../services/campaings.service';
import { useNavigate } from 'react-router-dom';
import Stepper1 from '../components/stepper-1-component';
import Stepper2 from '../components/stepper-2-component';
import Stepper3 from '../components/stepper-3-component';
import Stepper4 from '../components/stepper-4-fallback.component';
import Button from '../../../../components/forms/button.component';
import useStepper from '../../../../hooks/use-stepper.hook';
import Icon from '../../../../components/forms/icon.component';
import Navigate from '../../../../components/cards/header-navigate.component';
import Header from '../../../../components/layout/header.component';
import Asaid from '../../../../components/layout/asaid.component';
import Footer from '../../../../components/layout/footer.component';
import useBgPages from '../../../../hooks/use-bg-pages.hook';
import useCamapignsStructures from '../../../../hooks/use-structure-campaigns-fallback.hook';
import party from 'party-js';
import LogData from '../../../../components/logs/log-data.component';
import { stepsStandar, defaultValues } from '../constants/data.constants';

function AccountStepper() {
  useBgPages();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentStep } = useStepper('campaigns_standard');
  const { structureData } = useCamapignsStructures();
  const [isStep1Valid, setIsStep1Valid] = useState(false);
  const [isStep2Valid, setIsStep2Valid] = useState(false);
  const [isStep3Valid, setIsStep3Valid] = useState(false);
  const [isStep4Valid, setIsStep4Valid] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const methods = useForm({
    mode: 'onChange',
    defaultValues
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods;

  const {
    previewTemplateRcs: previewRCS,
    headersOptions: optionsExamples,
    vars: varsWa,
    channelsAvailable,
    account
  } = watch();

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return isStep1Valid;
      case 2:
        return isStep2Valid;
      case 3:
        return isStep3Valid;
      case 4:
        return isStep4Valid;
      default:
        return false;
    }
  };

  const isCurrentStepValid = validateStep(currentStep);

  const hasMinimumValidChannels = (channels: string[]): boolean => {
    if (!channels || channels.length === 0) return false;

    const uniqueChannels = new Set(
      channels.map((ch) =>
        ['rcs_basic', 'rcs_single', 'rcs_conversational'].includes(ch) ? 'rcs_grouped' : ch
      )
    );
    uniqueChannels.delete('0');

    return uniqueChannels.size >= 2;
  };

  const submit = async (data: any) => {
    const structure = structureData({
      ...data,
      previewRCS,
      optionsExamples,
      varsWa
    });

    if (structure?.error === 'variables_not_found' && Array.isArray(structure.variables)) {
      AlertService.toastInfo(
        '¡ Atención !, verifica el error en tus variables de tu plantilla RCS seleccionada.'
      );
      return;
    }

    const isConfirmed = await AlertService.question(
      '¡ Atención !',
      '¿Deseas guardar esta campaña con la información ingresada?',
      () => Promise.resolve(true),
      'Guardar campaña'
    );

    if (!isConfirmed) return;

    try {
      await CampaignsService.saveCampaigns(structure);
      AlertService.toastSuccess('¡ Éxito !, tu campaña se guardo correctamente');
      if (buttonRef.current) {
        party.confetti(buttonRef.current, {
          count: 200,
          speed: 450,
          spread: 190
        });
      }
      LogData(structure, 'saveCampaignFallBack-594956963495', window.location.href);
      setTimeout(() => {
        navigate('/sendCampaigns/resume', { state: { structure } });
      }, 1500);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  useEffect(() => {
    setValue('typeCampaign', 'FALLBACK');
  }, []);

  return (
    <div className="page d-flex flex-row flex-column-fluid">
      <Asaid />
      <div className="wrapper d-flex min-vh-100 flex-column flex-row-fluid" id="kt_wrapper">
        <Header />
        <div
          className="content fs-6 d-flex flex-column flex-column-fluid"
          id="kt_content"
          style={{
            backgroundImage: "url('/assets/media/img/bg-campaigns-2.png')",
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundAttachment: 'fixed'
          }}
        >
          <div className="post fs-6 d-flex flex-column-fluid mt-7" id="kt_post">
            <div className="container-xxl">
              <div className="mb-16">
                <Navigate
                  btnBack
                  title={
                    <>
                      <Icon icon="ki-medal-star" size="fs-3x" type="primary" />
                      <span className="ms-2">Campañas</span>
                    </>
                  }
                  titleBolder={'Fallback'}
                  subtitle={'Ingresa los datos necesarios de tu campaña.'}
                  breadcrumbItems={['Modalidades de campañas', 'Registro']}
                />
              </div>
              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(submit)}>
                  <div className="stepper" id="campaigns_standard">
                    <div className="d-flex justify-content-center align-items-center px-5 py-10">
                      <div className="row w-100 g-5">
                        <div
                          className={`col-12 col-md-4 sticky-box col-lg-${currentStep === 4 ? '3' : '4'}`}
                        >
                          <div
                            className="stepper stepper-pills stepper-column d-flex flex-column flex-lg-row mb-20"
                            id="add_new_area_stepper"
                          >
                            <div className="stepper-nav">
                              {stepsStandar.map((step, index) => (
                                <div
                                  key={`step-${step.number}`}
                                  className={`stepper-item me-5 ${step.isCurrent ? 'current' : ''}`}
                                  data-kt-stepper-element="nav"
                                >
                                  <div className="stepper-wrapper d-flex align-items-center">
                                    <div className="stepper-icon w-40px h-40px">
                                      <i className="stepper-check fas fa-check" />
                                      <span className="stepper-number">{step.number}</span>
                                    </div>
                                    <div className="stepper-label">
                                      <h3 className="stepper-title text-dark fw-bolder">
                                        {step.title}
                                      </h3>
                                      <div className="stepper-desc text-dark">
                                        {step.description}
                                      </div>
                                    </div>
                                  </div>
                                  {index !== stepsStandar.length - 1 && (
                                    <div className="stepper-line h-40px" />
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className={`col-12 col-md-8 col-lg-${currentStep === 4 ? '9' : '8'}`}>
                          <div className="card animate-fadeInUp animate-delay-1">
                            <div className="card-body">
                              <div
                                className="flex-column current"
                                data-kt-stepper-element="content"
                              >
                                <Stepper1
                                  onValidationChange={(isValid) => setIsStep1Valid(isValid)}
                                />
                              </div>
                              <div className="flex-column" data-kt-stepper-element="content">
                                <Stepper2
                                  onValidationChange={(isValid) => setIsStep2Valid(isValid)}
                                />
                              </div>
                              <div className="flex-column" data-kt-stepper-element="content">
                                <Stepper3
                                  onValidationChange={(isValid) => setIsStep3Valid(isValid)}
                                />
                              </div>
                              <div className="flex-column" data-kt-stepper-element="content">
                                <Stepper4
                                  onValidationChange={(isValid) => setIsStep4Valid(isValid)}
                                />
                              </div>
                            </div>
                            <div
                              className={`card-footer ${
                                !hasMinimumValidChannels(channelsAvailable) && account
                                  ? 'd-none'
                                  : ''
                              } d-flex justify-content-end`}
                            >
                              <div className="d-flex gap-3">
                                <Button
                                  type="button"
                                  typeStepperBtn="previous"
                                  id="previous"
                                  className="btn btn-lg btn-secondary hover-scale"
                                >
                                  <Icon icon="ki-left" size="fs-1" />
                                  {t('general.back')}
                                </Button>

                                <Button
                                  ref={buttonRef}
                                  type="submit"
                                  typeStepperBtn="submit"
                                  id="sendData"
                                  className="btn btn-lg btn-primary hover-scale"
                                  disabled={!isCurrentStepValid}
                                  isLoading={isSubmitting}
                                >
                                  {t('general.save')}
                                  <Icon icon="ki-send" size="fs-1" className="ms-2" />
                                </Button>

                                <Button
                                  type="button"
                                  className="btn btn-lg btn-primary hover-scale text-nowrap"
                                  id="next"
                                  typeStepperBtn="next"
                                  disabled={!isCurrentStepValid}
                                >
                                  {t('general.continue')} <Icon icon="ki-right" size="fs-1" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </FormProvider>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}

export default AccountStepper;
